import {
  CreateEnvironmentRequest,
  EnvironmentData,
  EnvironmentUpdateRequest,
  GetCurrentWorkspaceDetailsResponse,
  getRequestConfig,
  MemberData,
  MemberDataUpdateRequest,
  MemberListResponse,
  OrganizationData,
  OrganizationSettingsUpdateRequest,
  OrganizationsListResponse,
  OrganizationUserManagementSettings,
  withMakeRequest,
  WorkspaceData
} from './';
import Request from './Request.ts';
import { ConnectionData } from '~/api/types/connectionData.ts';
import {
  DomainData,
  DomainListResponse,
  DomainType
} from '~/api/types/domainData.ts';
import {
  GeneratePortalLinkResponse,
  PortalLink,
  PortalLinkListResponse
} from '~/api/types/portalLinks.ts';
import {
  PortalSessionDetailsResponse,
  PortalSessionResponse
} from '~/api/types/commons.ts';
import { CustomizationSettings } from '~/api/types/customisationData.ts';
import { CustomizationResponse } from './types/customisationData.ts';

import {
  UserProfileAttribute,
  UserProfileAttributeCreateRequest,
  UserProfileAttributeCreateResponse,
  UserProfileAttributesListResponse,
  UserProfileAttributeUpdateRequest,
  UserProfileAttributeUpdateResponse
} from '~/api/types/userProfileAttributes.ts';
import {
  FeatureFlagData,
  GetFeatureFlagRequest,
  OpenFeatureListResponse
} from '~/api/types/featureFlagData.ts';
import {
  CreateEnvironmentAssetUploadLinkResponse,
  EnvironmentAssetTypes
} from '~/api/types/environmentAssetData.ts';
import { GenerateWebhookPortalLinkResponse } from '~/api/types/webhookData.ts';
import {
  CreateDirectoryRequest,
  CreateDirectoryResponse,
  DirectoryData,
  DirectoryUser,
  GenerateDirectorySecretRequest,
  GenerateDirectorySecretResponse,
  ListDirectoryGroupRequest,
  ListDirectoryGroupResponse,
  ListDirectoryResponse,
  ListDirectoryUserRequest,
  ListDirectoryUserResponse
} from '~/api/types/directoryData.ts';
import {
  DirectoryEventTypes,
  EventData,
  EventsListResponse
} from '~/api/types/eventData.ts';
import camelcaseKeys from 'camelcase-keys';
import { SignupWithEmailRequest } from '~/api/types/signupData.ts';
import {
  EnvironmentSessionDataResponse,
  EnvironmentSessionSettings,
  UpdateEnvironmentSessionSettingsResponse
} from '~/api/types/envSessionData.ts';
import { GroupRoleMapping } from '~/api/types/roleData.ts';
import { EnvAppSessionDetailsResponse } from '~/api/types/envAppSessionData.ts';

const getFeatureFlags: (
  context: GetFeatureFlagRequest
) => Promise<FeatureFlagData[]> = withMakeRequest(
  async (context: GetFeatureFlagRequest) =>
    await Request.getInstance().post(
      `${window.location.origin}/ofrep/v1/evaluate/flags`,
      {
        context: {
          workspace_id: context.workspaceId,
          environment_type: context.environmentType,
          targetingKey: context.targetingKey,
          workspace_created_at: context.workspaceCreatedAt
        }
      },
      getRequestConfig({ snakeCaseRequest: false })
    ),
  (data: OpenFeatureListResponse) => data.flags || []
);

const signup: (request: SignupWithEmailRequest) => Promise<boolean> =
  withMakeRequest(
    async ({ email }: SignupWithEmailRequest) =>
      await Request.getInstance().post(`${window.location.origin}/app/signup`, {
        email
      })
  );

const getOrganizations: (
  pageToken: string,
  pageSize: number
) => Promise<OrganizationsListResponse> = withMakeRequest(
  async (pageToken: string, pageSize: number) => {
    return await Request.getInstance().get(
      `/organizations?page_token=${pageToken}&page_size=${pageSize}`,
      getRequestConfig({ addEnvDomain: true })
    );
  }
);

const searchOrganizations: (
  query: string,
  pageToken: string,
  pageSize: number
) => Promise<OrganizationsListResponse> = withMakeRequest(
  async (query: string, pageToken: number, pageSize: number) =>
    await Request.getInstance().get(
      `/organizations:search?query=${encodeURIComponent(query)}&page_token=${pageToken}&page_size=${pageSize}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

const getOrganizationById: (orgId: string) => Promise<OrganizationData> =
  withMakeRequest(
    async (orgId) =>
      await Request.getInstance().get(
        `/organizations/${orgId}`,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data) => data.organization
  );

const addOrganization: (
  orgData: OrganizationData
) => Promise<OrganizationData> = withMakeRequest(
  async (orgData: OrganizationData) =>
    await Request.getInstance().post(
      `/organizations`,
      orgData,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data) => data.organization as OrganizationData
);

const updateOrganization: (
  orgId: string,
  orgData: OrganizationData
) => Promise<OrganizationData> = withMakeRequest(
  async (orgId, orgData) =>
    await Request.getInstance().patch(
      `/organizations/${orgId}`,
      orgData,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data) => data.organization as OrganizationData
);

const updateOrganizationSettings: (
  request: OrganizationSettingsUpdateRequest
) => Promise<OrganizationData> = withMakeRequest(
  async (request: OrganizationSettingsUpdateRequest) =>
    await Request.getInstance().patch(
      `/organizations/${request.id}/settings`,
      request.settings,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data) => data.organization as OrganizationData
);

const deleteOrganizationById: (
  organizationId: string
) => Promise<OrganizationsListResponse> = withMakeRequest(
  async (organizationId) =>
    await Request.getInstance().delete(
      `/organizations/${organizationId}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

const getCurrentUserDetails: () => Promise<MemberData> = withMakeRequest(
  async () => await Request.getInstance().get('/members:this'),
  (data) => {
    return data.member as MemberData;
  }
);

const getCurrentWorkspaceDetails: () => Promise<GetCurrentWorkspaceDetailsResponse> =
  withMakeRequest(
    async () => await Request.getInstance().get('/workspaces:this'),
    (data: GetCurrentWorkspaceDetailsResponse) => data
  );

const getCurrentEnvironmentList: () => Promise<EnvironmentData[]> =
  withMakeRequest(
    async () => await Request.getInstance().get('/environments?page_size=100'),
    (data) => data.environments as Array<EnvironmentData> // Pagination ignored for now
  );

const createEnvironment: (
  data: CreateEnvironmentRequest
) => Promise<EnvironmentData> = withMakeRequest(
  async (data: EnvironmentData) =>
    await Request.getInstance().post('/environments', data),
  (data) => data.environment as EnvironmentData
);

const updateEnvironmentById: (
  environmentId: string,
  data: EnvironmentUpdateRequest
) => Promise<EnvironmentData> = withMakeRequest(
  async (envId: string, data: EnvironmentUpdateRequest) =>
    await Request.getInstance().patch(`/environments/${envId}`, data)
);

const createEnvironmentAssetUploadLink: (
  environmentId: string,
  category: EnvironmentAssetTypes,
  extension: string
) => Promise<CreateEnvironmentAssetUploadLinkResponse> = withMakeRequest(
  async (
    environmentId: string,
    category: EnvironmentAssetTypes,
    extension: string
  ) =>
    await Request.getInstance().post(
      `/environments/${environmentId}/asset`,
      { category, extension },
      getRequestConfig({ addEnvDomain: true })
    )
);

const uploadEnvironmentAsset: (url: string, file: File) => Promise<boolean> =
  withMakeRequest(
    async (url: string, file: File) =>
      await Request.getInstance().put(url, file, {
        headers: {
          'Content-Type': file.type
        }
      })
  );

const updateLoggedInMember: (
  data: MemberDataUpdateRequest | MemberData
) => Promise<MemberData> = withMakeRequest(
  async (data) => await Request.getInstance().patch('/members:this', data)
);

const updateWorkspace: (displayName: string) => Promise<WorkspaceData> =
  withMakeRequest(
    async (displayName) =>
      await Request.getInstance().patch('/workspaces:this', { displayName }),
    (data) => data.workspace as WorkspaceData
  );

const getWorkspaceMembers: () => Promise<MemberListResponse> = withMakeRequest(
  async () => await Request.getInstance().get('/members?pageSize=500')
);

const addMember: (m: MemberData) => Promise<MemberData> = withMakeRequest(
  async (memberData) =>
    await Request.getInstance().post('/members', memberData),
  (data) => data.member as MemberData
);

const deleteMember: (id: string) => Promise<boolean> = withMakeRequest(
  async (memberId) =>
    await Request.getInstance().delete(`/members/${memberId}`),
  () => true
);

const getOrganizationDomains: (
  organizationId: string,
  domainType?: DomainType
) => Promise<DomainListResponse> = withMakeRequest(
  async (organizationId, domainType) =>
    await Request.getInstance().get(
      `/organizations/${organizationId}/domains?${domainType ? `domain_type=${domainType}&` : ''}page_size=100`,
      getRequestConfig({ addEnvDomain: true })
    )
);

const addOrganizationDomain: (
  organizationId: string,
  data: DomainData
) => Promise<DomainData> = withMakeRequest(
  async (organizationId, data) =>
    await Request.getInstance().post(
      `/organizations/${organizationId}/domains`,
      data,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data) => data.domain
);

const deleteOrganizationDomain: (
  organizationId: string,
  domainId: string
) => Promise<boolean> = withMakeRequest(
  async (organizationId, domainId) =>
    await Request.getInstance().delete(
      `/organizations/${organizationId}/domains/${domainId}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

const getPortalLinksForOrg: (organizationId: string) => Promise<PortalLink[]> =
  withMakeRequest(
    async (organizationId) =>
      await Request.getInstance().get(
        `/organizations/${organizationId}/portal_links`,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: PortalLinkListResponse) => data.links
  );
const generatePortalLink: (organizationId: string) => Promise<PortalLink> =
  withMakeRequest(
    async (organizationId) =>
      await Request.getInstance().put(
        `/organizations/${organizationId}/portal_links`,
        {},
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: GeneratePortalLinkResponse) => data.link || {}
  );

const deletePortalLink: (organizationId: string) => Promise<boolean> =
  withMakeRequest(
    async (organizationId) =>
      await Request.getInstance().delete(
        `/organizations/${organizationId}/portal_links`,
        getRequestConfig({ addEnvDomain: true })
      ),
    () => true
  );

const createPortalSession: (
  portalLinkId: string
) => Promise<PortalSessionResponse> = withMakeRequest(
  async (portalLinkId) =>
    await Request.getInstance().post(
      `${window.location.origin}/sessions:portal`,
      {
        token: portalLinkId
      }
    )
);

const getPortalSessionDetails: () => Promise<PortalSessionDetailsResponse> =
  withMakeRequest(
    async () =>
      await Request.getInstance().get(
        `/environments/-/sessions:me`,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data) => data || {}
  );

const getEnvAppSessionDetails: () => Promise<EnvAppSessionDetailsResponse> =
  withMakeRequest(
    async () =>
      await Request.getInstance().get(
        `/users:this`,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data) => data || {}
  );

const getCurrentPortalCustomizationConfig: () => Promise<CustomizationResponse | null> =
  withMakeRequest(
    async () =>
      await Request.getInstance().get(
        `/environments/-/portal_customizations`,
        getRequestConfig({ addEnvDomain: true })
      )
  );

const getCustomizationSettingsForEnvironment: () => Promise<CustomizationResponse> =
  withMakeRequest(
    async () =>
      await Request.getInstance().get(
        `/portal_customizations`,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: CustomizationResponse) => data || {}
  );

const updatePortalCustomizationSettingsForEnvironment: (
  envId: string,
  data: CustomizationSettings
) => Promise<CustomizationSettings> = withMakeRequest(
  async (envId: string, data: CustomizationSettings) =>
    await Request.getInstance().put(
      `/environments/${envId}/portal_customizations`,
      data,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: CustomizationResponse) => data.customizationSettings || {}
);

const getUserProfileAttributes: () => Promise<UserProfileAttribute[]> =
  withMakeRequest(
    async () =>
      await Request.getInstance().get(
        `/user-profile-attributes`,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: UserProfileAttributesListResponse) => data.userAttributes
  );

const createUserProfileAttribute: (
  attributeObj: UserProfileAttributeCreateRequest | UserProfileAttribute
) => Promise<UserProfileAttribute> = withMakeRequest(
  async (attributeObj) =>
    await Request.getInstance().post(
      `/user-profile-attributes`,
      attributeObj,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: UserProfileAttributeCreateResponse) => data.userAttribute
);

const updateUserProfileAttributeByKey: (
  attributeObj: UserProfileAttributeUpdateRequest | UserProfileAttribute
) => Promise<UserProfileAttribute> = withMakeRequest(
  async (attributeObj) =>
    await Request.getInstance().patch(
      `/user-profile-attributes/${attributeObj.key}`,
      attributeObj,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: UserProfileAttributeUpdateResponse) => data.userAttribute
);

const deleteUserProfileAttributeByKey: (
  attributeKey: string
) => Promise<boolean> = withMakeRequest(
  async (attributeKey) =>
    await Request.getInstance().delete(
      `/user-profile-attributes/${attributeKey}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

const getWebhookConsumerPortal: () => Promise<GenerateWebhookPortalLinkResponse> =
  withMakeRequest(
    async () =>
      await Request.getInstance().get(
        '/webhooks/portal-url',
        getRequestConfig({ addEnvDomain: true })
      )
  );

const getDirectoriesForOrganization: (
  organizationId: string
) => Promise<DirectoryData[]> = withMakeRequest(
  async (organizationId: string) =>
    await Request.getInstance().get(
      `/organizations/${organizationId}/directories?page_size=100`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: ListDirectoryResponse) => data.directories
);

const createDirectoryForOrganization: (
  organizationId: string,
  data: CreateDirectoryRequest
) => Promise<DirectoryData> = withMakeRequest(
  async (organizationId: string, data: CreateDirectoryRequest) =>
    await Request.getInstance().post(
      `/organizations/${organizationId}/directories`,
      data,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: CreateDirectoryResponse) => data.directory
);

const getDirectoryDetailsById: (
  organizationId: string,
  directoryId: string
) => Promise<DirectoryData> = withMakeRequest(
  async (organizationId: string, directoryId: string) =>
    await Request.getInstance().get(
      `/organizations/${organizationId}/directories/${directoryId}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  ({ directory }): DirectoryData => {
    const { roleAssignments, ...filteredDirectoryData } = directory;
    return {
      ...filteredDirectoryData,
      ...(roleAssignments && {
        roleAssignments: {
          assignments: roleAssignments.assignments.map(
            ({ roleId, ...filteredAssignmentData }) => filteredAssignmentData
          ) as GroupRoleMapping[]
        }
      })
    } as DirectoryData;
  }
);

const updateDirectoryById: (
  organizationId: string,
  directory: DirectoryData
) => Promise<DirectoryData> = withMakeRequest(
  async (organizationId: string, directory: DirectoryData) =>
    await Request.getInstance().patch(
      `/organizations/${organizationId}/directories/${directory.id}`,
      directory,
      getRequestConfig({ addEnvDomain: true })
    )
);

const toggleDirectoryStatus: (
  organizationId: string,
  directoryId: string,
  shouldEnable: boolean
) => Promise<DirectoryData> = withMakeRequest(
  async (organizationId: string, directoryId: string, shouldEnable: boolean) =>
    await Request.getInstance().patch(
      `/organizations/${organizationId}/directories/${directoryId}:${shouldEnable ? 'enable' : 'disable'}`,
      {},
      getRequestConfig({ addEnvDomain: true })
    )
);

const getDirectoryEvents: (
  organizationId: string,
  pageToken: string,
  pageSize: number
) => Promise<EventsListResponse> = withMakeRequest(
  async (organizationId: string, pageToken: string, pageSize: number) =>
    await Request.getInstance().post(
      `/events?page_token=${pageToken}&page_size=${pageSize}`,
      {
        organization_id: organizationId,
        event_types: Object.values(DirectoryEventTypes)
      },
      getRequestConfig({ addEnvDomain: true, camelCaseResponse: false })
    ),
  (data: EventsListResponse) => {
    return {
      ...camelcaseKeys(data as Record<any, any>, { deep: true }),
      events: data.events.map((event) => ({
        ...camelcaseKeys(event as Record<any, any>, {
          deep: true
        }),
        rawPayload: JSON.stringify(event, undefined, 2)
      })) as EventData[]
    };
  }
);

const getDirectoryUsersList: (
  request: ListDirectoryUserRequest
) => Promise<ListDirectoryUserResponse> = withMakeRequest(
  async (request: ListDirectoryUserRequest) =>
    Request.getInstance().get(
      `/organizations/${request.organizationId}/directories/${request.directoryId}/users?include_detail=${request.groupId ? 'false' : 'true'}&page_token=${request.pageToken}&page_size=${request.pageSize}${request.groupId ? `&directory_group_id=${request.groupId}` : ''}`,
      getRequestConfig({ addEnvDomain: true, camelCaseResponse: false })
    ),
  (data: ListDirectoryUserResponse) => {
    return {
      ...camelcaseKeys(data as Record<any, any>, { deep: true }),
      users: data.users.map((user: Record<any, any>) => ({
        ...camelcaseKeys(user as Record<any, any>, {
          deep: true
        }),
        userDetail: user.user_detail,
        userDetailRaw: JSON.stringify(user.user_detail, undefined, 2)
      })) as DirectoryUser[]
    };
  }
);

const getDirectoryGroupList: (
  request: ListDirectoryGroupRequest
) => Promise<ListDirectoryGroupResponse> = withMakeRequest(
  async (request: ListDirectoryGroupRequest) =>
    Request.getInstance().get(
      `/organizations/${request.organizationId}/directories/${request.directoryId}/groups?page_token=${request.pageToken}&page_size=${request.pageSize}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

const getDirectoryGroupsSummaryList: (
  request: Partial<ListDirectoryGroupRequest>
) => Promise<ListDirectoryGroupResponse> = withMakeRequest(
  async (request: Partial<ListDirectoryGroupRequest>) =>
    Request.getInstance().get(
      `/organizations/${request.organizationId}/directories/${request.directoryId}/groups/summary`,
      getRequestConfig({ addEnvDomain: true })
    )
);

const getDirectoryExternalGroupsList: (
  request: Partial<ListDirectoryGroupRequest>
) => Promise<ListDirectoryGroupResponse> = withMakeRequest(
  async (request: Partial<ListDirectoryGroupRequest>) =>
    Request.getInstance().get(
      `/organizations/${request.organizationId}/directories/${request.directoryId}/groups?include_external_groups=true`,
      getRequestConfig({ addEnvDomain: true })
    )
);

const assignDirectoryGroups: (
  organizationId: string,
  directoryId: string,
  externalIds: string[]
) => Promise<boolean> = withMakeRequest(
  async (organizationId: string, directoryId: string, externalIds: string[]) =>
    Request.getInstance().put(
      `/organizations/${organizationId}/directories/${directoryId}/groups:assign`,
      {
        externalIds
      },
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

const syncDirectory: (
  organizationId: string,
  directoryId: string
) => Promise<boolean> = withMakeRequest(
  async (organizationId: string, directoryId: string) =>
    Request.getInstance().get(
      `/organizations/${organizationId}/directories/${directoryId}:sync`,
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

const generateDirectoryBearerToken: (
  request: GenerateDirectorySecretRequest
) => Promise<GenerateDirectorySecretResponse> = withMakeRequest(
  async ({
    organizationId,
    directoryId,
    shouldRegenerate = false
  }: GenerateDirectorySecretRequest) =>
    Request.getInstance().post(
      `/organizations/${organizationId}/directories/${directoryId}/secrets${shouldRegenerate ? ':regenerate' : ''}`,
      {},
      getRequestConfig({ addEnvDomain: true })
    )
);

type GetOrganizationUserManagementSettingsResponse = {
  settings: OrganizationUserManagementSettings;
};

const getOrganizationUserManagementSettings: (
  organizationId: string
) => Promise<OrganizationUserManagementSettings> = withMakeRequest(
  async (organizationId: string) =>
    await Request.getInstance().get(
      `/organizations/${organizationId}/settings/usermanagement`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: GetOrganizationUserManagementSettingsResponse) => data.settings
);

type UpdateOrgUserManagementSettingsRequest = {
  organizationId: string;
  settings: OrganizationUserManagementSettings;
};

type UpdateOrgUserManagementSettingsResponse = {
  settings: OrganizationUserManagementSettings;
};

const updateOrgUserManagementSettings: (
  request: UpdateOrgUserManagementSettingsRequest
) => Promise<OrganizationUserManagementSettings> = withMakeRequest(
  async ({
    organizationId,
    settings
  }: UpdateOrgUserManagementSettingsRequest) =>
    await Request.getInstance().patch(
      `/organizations/${organizationId}/settings/usermanagement`,
      {
        settings
      },
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: UpdateOrgUserManagementSettingsResponse) => data.settings
);

const getSessionSettingsForEnvironment: (
  envId: string
) => Promise<EnvironmentSessionSettings> = withMakeRequest(
  async (envId: string) =>
    await Request.getInstance().get(
      `/environments/${envId}/session-settings`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: EnvironmentSessionDataResponse) => data.sessionSettings || {}
);

const updateSessionSettingsForEnvironment: (
  envId: string,
  sessionData: EnvironmentSessionSettings
) => Promise<ConnectionData> = withMakeRequest(
  async (envId: string, sessionData: EnvironmentSessionSettings) =>
    await Request.getInstance().patch(
      `/environments/${envId}/session-settings`,
      { ...sessionData },
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: UpdateEnvironmentSessionSettingsResponse) => data.sessionSettings
);
export {
  getFeatureFlags,
  signup,
  getOrganizations,
  searchOrganizations,
  getOrganizationById,
  addOrganization,
  updateOrganization,
  updateOrganizationSettings,
  deleteOrganizationById,
  getCurrentUserDetails,
  getCurrentWorkspaceDetails,
  getCurrentEnvironmentList,
  createEnvironment,
  updateEnvironmentById,
  createEnvironmentAssetUploadLink,
  uploadEnvironmentAsset,
  updateLoggedInMember,
  updateWorkspace,
  getWorkspaceMembers,
  addMember,
  deleteMember,
  getOrganizationDomains,
  addOrganizationDomain,
  deleteOrganizationDomain,
  getPortalLinksForOrg,
  generatePortalLink,
  deletePortalLink,
  createPortalSession,
  getPortalSessionDetails,
  getEnvAppSessionDetails,
  getCurrentPortalCustomizationConfig,
  getCustomizationSettingsForEnvironment,
  updatePortalCustomizationSettingsForEnvironment,
  getUserProfileAttributes,
  createUserProfileAttribute,
  updateUserProfileAttributeByKey,
  deleteUserProfileAttributeByKey,
  getDirectoriesForOrganization,
  createDirectoryForOrganization,
  getDirectoryDetailsById,
  updateDirectoryById,
  toggleDirectoryStatus,
  getDirectoryEvents,
  getWebhookConsumerPortal,
  getDirectoryUsersList,
  getDirectoryGroupList,
  getDirectoryExternalGroupsList,
  getDirectoryGroupsSummaryList,
  assignDirectoryGroups,
  syncDirectory,
  generateDirectoryBearerToken,
  getSessionSettingsForEnvironment,
  updateSessionSettingsForEnvironment,
  getOrganizationUserManagementSettings,
  updateOrgUserManagementSettings
};
